/*
  Sidebar model for the grouped app shell (02-dashboard #8): the same labels and routes as today,
  in five groups — Workspace (no heading) / Delivery / Supply / Finance / Tools.

    import { NAV_GROUPS, groupNavItems, isNavItemActive } from '@/lib/nav';
    const groups = groupNavItems(NAV_ITEMS.filter((item) => hasPermission(user, item.permission)));
    groups.map((group) => (
      <div key={group.key}>
        {group.showLabel ? <div className="ui-sb-section">{group.label}</div> : null}
        {group.items.map((item) => <NavLink … active={isNavItemActive(item.path, pathname)}
                                     count={<SidebarCount count={counts[item.path]} />} />)}
      </div>
    ));

  Layout.js keeps NAV_ITEMS (icons, permissions, labels); this file only orders and groups them.
  groupNavItems(items, groups = NAV_GROUPS): items keep the group's `paths` order; an item whose path
  is not listed joins the group named by its `section`, else a trailing "More" group. Groups with no
  visible item (permissions) are dropped.
*/

export const NAV_GROUPS = [
  { key: 'workspace', label: 'Workspace', showLabel: false, paths: ['/', '/tasks'] },
  { key: 'delivery', label: 'Delivery', showLabel: true, paths: ['/projects', '/shop-drawings', '/documents'] },
  { key: 'supply', label: 'Supply', showLabel: true, paths: ['/inventory', '/procurement'] },
  { key: 'finance', label: 'Finance', showLabel: true, paths: ['/invoicing', '/forecast'] },
  { key: 'tools', label: 'Tools', showLabel: true, paths: ['/ai', '/admin'] },
];

/** '/' matches only the dashboard; other items match their path and anything below it. */
export function isNavItemActive(itemPath, pathname) {
  if (!itemPath || typeof pathname !== 'string') return false;
  if (itemPath === '/') return pathname === '/';
  return pathname === itemPath || pathname.startsWith(`${itemPath}/`) || pathname.startsWith(`${itemPath}?`);
}

export function groupNavItems(items = [], groups = NAV_GROUPS) {
  const result = groups.map((group) => ({ ...group, items: [] }));
  const byLabel = new Map(result.map((group) => [group.label.toLowerCase(), group]));
  const more = { key: 'more', label: 'More', showLabel: true, paths: [], items: [] };
  const placed = new Set();

  result.forEach((group) => {
    group.paths.forEach((path) => {
      const item = items.find((entry) => entry && entry.path === path);
      if (item && !placed.has(item)) {
        group.items.push(item);
        placed.add(item);
      }
    });
  });

  items.forEach((item) => {
    if (!item || placed.has(item)) return;
    const target = (item.section && byLabel.get(String(item.section).toLowerCase())) || more;
    target.items.push(item);
    placed.add(item);
  });

  return [...result, more].filter((group) => group.items.length > 0);
}

/** The group label of the page at `pathname` ('Delivery' for /projects/123) — for breadcrumbs. */
export function navGroupOf(pathname, items = [], groups = NAV_GROUPS) {
  const item = items.find((entry) => entry && isNavItemActive(entry.path, pathname));
  if (!item) return null;
  const group = groupNavItems(items, groups).find((entry) => entry.items.includes(item));
  return group ? group.label : null;
}
